import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Panel } from "@/components/Panel";
import { toast } from "sonner";
import {
  Briefcase, RefreshCw, TrendingUp, TrendingDown, X, ChevronRight,
  CheckCircle2, Lock, Compass, Sparkles,
} from "lucide-react";

// Phase 36.2 — Hedge Ledger
// Shows accepted hedging programmes with mark-to-market P&L per leg / per
// commodity / aggregate. Lets the operator close out a position to lock in
// the realised P&L.

const fmtUsd = (n) => {
  if (n === null || n === undefined) return "—";
  const abs = Math.abs(n);
  const sign = n < 0 ? "-" : "";
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(1)}K`;
  return `${sign}$${abs.toFixed(0)}`;
};

const fmtNum = (n, dp = 0) => {
  if (n === null || n === undefined) return "—";
  return n.toLocaleString(undefined, { maximumFractionDigits: dp });
};

export default function HedgeLedger() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [closingId, setClosingId] = useState(null);
  const [filter, setFilter] = useState("accepted"); // accepted | all
  const [seeding, setSeeding] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/hedge-ledger");
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load hedge ledger");
    } finally {
      setLoading(false);
    }
  };

  const seedDemo = async () => {
    setSeeding(true);
    try {
      const r = await api.post("/hedge-ledger/seed-demo");
      if (r.data.already_existed) {
        toast.info("Demo programme already exists — refreshing.");
      } else {
        toast.success(`Demo hedge added · ${r.data.commodity_id?.replace(/_/g, " ")}`);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to seed demo");
    } finally {
      setSeeding(false);
    }
  };

  useEffect(() => { load(); }, []);

  const closeHedge = async (rec) => {
    setClosingId(rec.id);
    try {
      const r = await api.post(`/commodities/${rec.commodity_id}/hedging-copilot/${rec.id}/close`);
      toast.success(`Position closed — realised ${fmtUsd(r.data.realised_pnl_usd)}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Close failed");
    } finally {
      setClosingId(null);
    }
  };

  const recordsForView = (data?.records || []).filter(r => {
    if (filter === "accepted") return r.status === "accepted";
    return true;
  });

  return (
    <div className="p-4 sm:p-6 space-y-5" data-testid="hedge-ledger-page">
      {/* HEADER */}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
            // TREASURY · MARK-TO-MARKET LEDGER
          </div>
          <h1 className="font-heading font-black text-2xl sm:text-3xl lg:text-4xl tracking-tight mt-1">
            Hedge Ledger
          </h1>
          <p className="text-xs text-zinc-500 max-w-xl mt-1.5">
            Every accepted hedging programme, marked to the live commodity index. Close positions
            here to lock in realised P&amp;L.
          </p>
        </div>
        <button
          onClick={load}
          data-testid="ledger-refresh-btn"
          disabled={loading}
          className="border border-zinc-800 hover:border-zinc-700 px-3 py-2 font-mono text-[11px] uppercase tracking-widest text-zinc-300 flex items-center gap-2"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          REFRESH MTM
        </button>
      </div>

      {/* AGGREGATE STRIP */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-900 border border-zinc-900">
        <Stat
          testId="ledger-active-count"
          label="ACTIVE HEDGES"
          value={fmtNum(data?.active_count)}
          accent="white"
          sub={`OF ${fmtNum(data?.total_count || 0)} TOTAL`}
        />
        <Stat
          testId="ledger-total-notional"
          label="TOTAL NOTIONAL"
          value={fmtUsd(data?.total_notional_usd)}
          accent="amber"
        />
        <Stat
          testId="ledger-total-mtm"
          label="MARK-TO-MARKET P&L"
          value={fmtUsd(data?.total_mtm_usd)}
          accent={(data?.total_mtm_usd ?? 0) >= 0 ? "green" : "red"}
        />
        <Stat
          testId="ledger-last-refresh"
          label="LAST REFRESH"
          value={data?.as_of ? new Date(data.as_of).toLocaleTimeString() : "—"}
          accent="white"
          sub={data?.as_of ? new Date(data.as_of).toUTCString().slice(5, 16) : ""}
        />
      </div>

      {/* BY-COMMODITY */}
      {(data?.by_commodity || []).length > 0 && (
        <Panel title="// BY COMMODITY" testId="ledger-by-commodity">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-px bg-zinc-900">
            {data.by_commodity.map(c => {
              const up = c.mtm_usd >= 0;
              return (
                <div key={c.commodity_id} className="bg-black p-3" data-testid={`ledger-comm-${c.commodity_id}`}>
                  <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">
                    {c.commodity_id.replace(/_/g, " ")}
                  </div>
                  <div className={`font-mono text-lg font-medium mt-1 ${up ? "text-[#00E676]" : "text-[#FF3B30]"}`}>
                    {fmtUsd(c.mtm_usd)}
                  </div>
                  <div className="font-mono text-[10px] text-zinc-600 mt-0.5">
                    {fmtNum(c.qty, 1)} · {c.leg_count} leg{c.leg_count === 1 ? "" : "s"}
                  </div>
                  <div className="font-mono text-[10px] text-zinc-700 mt-0.5">
                    spot {fmtNum(c.current_spot, 2)}
                  </div>
                </div>
              );
            })}
          </div>
        </Panel>
      )}

      {/* RECORDS */}
      <Panel
        title="// PROGRAMMES"
        meta={
          <div className="flex items-center gap-1">
            {[
              { key: "accepted", label: "ACTIVE" },
              { key: "all", label: "ALL" },
            ].map(t => (
              <button
                key={t.key}
                onClick={() => setFilter(t.key)}
                data-testid={`ledger-filter-${t.key}`}
                className={`px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest border ${
                  filter === t.key
                    ? "border-[#FFB000] text-[#FFB000] bg-[#FFB000]/10"
                    : "border-zinc-800 text-zinc-500"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        }
        testId="ledger-records"
      >
        {loading && (
          <div className="p-6 text-center font-mono text-xs text-zinc-600">LOADING…</div>
        )}
        {!loading && recordsForView.length === 0 && (
          <div className="p-10 text-center" data-testid="ledger-empty-state">
            <Briefcase className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
            <div className="font-mono text-sm text-zinc-300">
              No {filter === "accepted" ? "active" : ""} programmes yet.
            </div>
            <div className="font-mono text-[11px] text-zinc-500 mt-1.5 max-w-md mx-auto leading-relaxed">
              Hedging programmes show up here once you generate one on the Commodity Desk
              and click <span className="text-[#00E676] font-bold">ACCEPT PROGRAMME</span>.
            </div>
            <Link
              to="/app/commodities#hedging-copilot"
              data-testid="ledger-empty-cta"
              className="mt-5 inline-flex items-center gap-2 bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-widest px-5 py-2.5 hover:opacity-90"
            >
              <Compass className="w-3.5 h-3.5" /> GENERATE YOUR FIRST HEDGE
              <ChevronRight className="w-3.5 h-3.5" />
            </Link>
            <div className="mt-3 flex items-center justify-center gap-2 font-mono text-[10px] uppercase tracking-widest text-zinc-600">
              <span className="h-px w-8 bg-zinc-800" /> OR <span className="h-px w-8 bg-zinc-800" />
            </div>
            <button
              onClick={seedDemo}
              disabled={seeding}
              data-testid="ledger-seed-demo-btn"
              className="mt-3 inline-flex items-center gap-2 border border-zinc-700 hover:border-[#FFB000] hover:text-[#FFB000] text-zinc-300 font-mono text-[11px] uppercase tracking-widest px-4 py-2 disabled:opacity-50"
            >
              {seeding ? (
                <><RefreshCw className="w-3.5 h-3.5 animate-spin" /> SEEDING…</>
              ) : (
                <><Sparkles className="w-3.5 h-3.5" /> TRY IT WITH SAMPLE DATA</>
              )}
            </button>
            <div className="font-mono text-[10px] text-zinc-700 mt-3">
              Pro tip · You'll need a CSV under Data Sources with delivery date + qty columns.
            </div>
          </div>
        )}

        <div className="divide-y divide-zinc-900">
          {recordsForView.map(rec => (
            <div key={rec.id} className="p-4 hover:bg-zinc-950/30" data-testid={`ledger-record-${rec.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-heading font-bold text-base tracking-tight">
                      {rec.commodity_id?.replace(/_/g, " ").toUpperCase()}
                    </span>
                    <StatusPill status={rec.status} />
                    <span className="font-mono text-[10px] text-zinc-500">{rec.recommended_band}</span>
                    <span className="font-mono text-[10px] text-zinc-600">{rec.side?.toUpperCase()}</span>
                  </div>
                  <div className="font-mono text-[10px] text-zinc-600 mt-0.5">
                    Accepted {rec.accepted_at ? new Date(rec.accepted_at).toLocaleString() : "—"}
                    {" · "}spot at accept {fmtNum(rec.spot_at_accept, 2)} → live {fmtNum(rec.current_spot, 2)}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">MTM P&amp;L</div>
                    <div className={`font-mono text-lg font-medium ${rec.total_mtm_usd >= 0 ? "text-[#00E676]" : "text-[#FF3B30]"}`}>
                      {rec.total_mtm_usd >= 0 ? <TrendingUp className="inline w-3.5 h-3.5 mr-1" />
                        : <TrendingDown className="inline w-3.5 h-3.5 mr-1" />}
                      {fmtUsd(rec.total_mtm_usd)}
                    </div>
                  </div>
                  {rec.status === "accepted" && (
                    <button
                      onClick={() => closeHedge(rec)}
                      disabled={closingId === rec.id}
                      data-testid={`ledger-close-${rec.id}`}
                      className="border border-zinc-800 hover:border-[#FFB000] hover:text-[#FFB000] px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest text-zinc-400 disabled:opacity-50 flex items-center gap-1.5"
                    >
                      <Lock className="w-3 h-3" />
                      {closingId === rec.id ? "CLOSING…" : "CLOSE OUT"}
                    </button>
                  )}
                </div>
              </div>

              {/* Legs */}
              {(rec.legs || []).length > 0 && (
                <div className="mt-3 grid grid-cols-1 lg:grid-cols-3 gap-2">
                  {rec.legs.map((leg, i) => (
                    <div key={i} className="border border-zinc-900 bg-zinc-950/40 p-2" data-testid={`ledger-leg-${rec.id}-${i + 1}`}>
                      <div className="flex items-center justify-between">
                        <span className={`px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase tracking-widest border ${
                          leg.leg_side === "BUY"
                            ? "border-[#00E676]/40 text-[#00E676] bg-[#00E676]/5"
                            : "border-[#FF3B30]/40 text-[#FF3B30] bg-[#FF3B30]/5"
                        }`}>
                          {leg.leg_side}
                        </span>
                        <span className="font-mono text-[9px] text-[#FFB000]">{leg.tenor || "—"}</span>
                      </div>
                      <div className="font-mono text-[10px] text-zinc-400 mt-1 truncate">
                        {leg.instrument || rec.commodity_id}
                      </div>
                      <div className="grid grid-cols-3 gap-1 mt-2">
                        <Mini label="QTY" value={fmtNum(leg.qty, 1)} />
                        <Mini label="REF" value={fmtNum(leg.reference_price, 2)} />
                        <Mini
                          label="MTM"
                          value={fmtUsd(leg.mtm_usd)}
                          accent={leg.mtm_usd >= 0 ? "green" : "red"}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </Panel>

      {/* Footer hint */}
      <div className="font-mono text-[9px] text-zinc-600 leading-relaxed border-l-2 border-zinc-900 pl-3">
        Mark-to-market math is simulated against DARA's commodity index — production deployments
        should replace the index with a licensed Platts / LME feed for board-grade accuracy.
        Realised P&amp;L is locked in when you click CLOSE OUT.
      </div>
    </div>
  );
}

function Stat({ label, value, accent = "white", sub, testId }) {
  const cls = accent === "red" ? "text-[#FF3B30]"
    : accent === "green" ? "text-[#00E676]"
    : accent === "amber" ? "text-[#FFB000]" : "text-white";
  return (
    <div className="bg-black p-4" data-testid={testId}>
      <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`font-mono text-2xl font-medium mt-1 ${cls}`}>{value}</div>
      {sub && <div className="font-mono text-[9px] text-zinc-600 mt-1">{sub}</div>}
    </div>
  );
}

function Mini({ label, value, accent = "white" }) {
  const cls = accent === "red" ? "text-[#FF3B30]"
    : accent === "green" ? "text-[#00E676]" : "text-white";
  return (
    <div>
      <div className="font-mono text-[8px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`font-mono text-xs ${cls}`}>{value}</div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    accepted: "border-[#00E676]/40 text-[#00E676] bg-[#00E676]/5",
    closed: "border-[#FFB000]/40 text-[#FFB000] bg-[#FFB000]/5",
    dismissed: "border-zinc-700 text-zinc-500 bg-zinc-900/40",
    draft: "border-zinc-700 text-zinc-400 bg-zinc-900/40",
  };
  const s = (status || "draft").toLowerCase();
  return (
    <span className={`px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-widest border ${map[s] || map.draft}`}>
      {s === "accepted" && <CheckCircle2 className="inline w-3 h-3 mr-0.5" />}
      {s === "closed" && <Lock className="inline w-3 h-3 mr-0.5" />}
      {s === "dismissed" && <X className="inline w-3 h-3 mr-0.5" />}
      {s.toUpperCase()}
    </span>
  );
}
